import { useState, useEffect, type ReactNode } from 'react';
import { useAuth } from '@/hooks/use-auth-compat';
import { Link, useNavigate } from 'react-router-dom';
import { Lock, Pencil, Check, ArrowRight, Coins, AlertCircle, MessageCircle, Sparkles } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { getGoalProfile } from '@/services/goalProfileService';
import { createInquiry } from '@/services/inquiryService';
import { listExamTargets, daysUntil } from '@/services/examTargetService';
import { useOneToOneSearch } from '@/hooks/use-offering-search';
import { useStudentProfile } from '@/contexts/StudentProfileContext';
import { gradeLabel, modeLabel, timelineLabel } from '@/lib/goalOptions';
import { currencySymbol } from '@/lib/currency';
import type { GoalProfileResponse } from '@/types/goalProfile';
import type { StudentExamTarget } from '@/types/registeredStudent';
import { INQUIRY_COST_COINS } from '@/services/inquiryService';

interface InquiryComposerModalProps {
  teacherUserId: string;
  teacherName: string | null;
  teacherHandle: string;
  offeringId?: string;
  open: boolean;
  onClose: () => void;
}

function budgetText(p: GoalProfileResponse | null): string {
  if (!p?.budgetMin && !p?.budgetMax) return '';
  const c = currencySymbol(p.currency);
  if (p.budgetMin && p.budgetMax) return `${c}${p.budgetMin}–${c}${p.budgetMax}`;
  return `${c}${p.budgetMax ?? p.budgetMin}`;
}

function buildStandardMessage(teacherName: string | null, p: GoalProfileResponse | null): string {
  const first = teacherName?.split(' ')[0] || 'there';
  const lines = [`Hi ${first},`, ''];
  if (p?.subject && p?.gradeBand) lines.push(`I'm looking for help with ${p.subject} for ${gradeLabel(p.gradeBand) || p.gradeBand}.`);
  else if (p?.subject) lines.push(`I'm looking for help with ${p.subject}.`);
  else lines.push("I'm interested in your teaching and would love to learn more.");
  if (p?.goalType) lines.push(`My goal is ${p.goalType.toLowerCase()}.`);
  if (p?.modePreference) lines.push(`I'd prefer ${modeLabel(p.modePreference).toLowerCase()} sessions.`);
  const b = budgetText(p);
  if (b) lines.push(`My budget is around ${b} per session.`);
  if (p?.startTimeline) lines.push(`I'm hoping to start ${timelineLabel(p.startTimeline).toLowerCase()}.`);
  lines.push('', 'Would love to know if you have availability and whether a trial is possible.');
  return lines.join('\n');
}

function buildExamMessage(teacherName: string | null, p: GoalProfileResponse | null, exam: StudentExamTarget): string {
  const first = teacherName?.split(' ')[0] || 'there';
  const d = daysUntil(exam.examDate);
  const subj = p?.subject ?? 'my subject';
  const lines = [
    `Hi ${first},`, '',
    `I'm preparing for ${exam.examName}${d >= 0 ? ` (about ${d} days away)` : ''} and need focused help with ${subj}.`,
  ];
  if (exam.targetScore) lines.push(`I'm targeting ${exam.targetScore}.`);
  const b = budgetText(p);
  if (b || p?.modePreference) lines.push(`${b ? `Budget around ${b} per session` : ''}${b && p?.modePreference ? ' · ' : ''}${p?.modePreference ? modeLabel(p.modePreference) : ''}.`);
  lines.push('', `Would love to understand your approach to ${exam.examName} prep and whether you have availability.`);
  return lines.join('\n');
}

/** Coloured badge summarising one piece of student context. */
function ContextBadge({ label, value }: { label: string; value: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 border border-indigo-100 px-2.5 py-1 text-xs font-medium text-indigo-700 whitespace-nowrap">
      <span className="text-indigo-400 font-normal">{label}:</span> {value}
    </span>
  );
}

/** Avatar circle built from the teacher's initials. */
function TeacherAvatar({ name }: { name: string }) {
  const initials = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
  return (
    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-violet-500 flex items-center justify-center text-white text-sm font-bold shrink-0 shadow-sm">
      {initials || '?'}
    </div>
  );
}

export function InquiryComposerModal({
  teacherUserId, teacherName, teacherHandle, offeringId, open, onClose,
}: InquiryComposerModalProps) {
  const displayName = teacherName?.trim() || 'this teacher';
  const firstName = teacherName?.trim().split(' ')[0] || 'the teacher';
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [goalProfile, setGoalProfile] = useState<GoalProfileResponse | null>(null);
  const [examTargets, setExamTargets] = useState<StudentExamTarget[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<ReactNode | null>(null);
  const [sent, setSent] = useState(false);

  const auth = useAuth();
  const { profile: studentProfile, refreshProfile } = useStudentProfile();
  const coinBalance = studentProfile?.coinBalance ?? null;

  useEffect(() => {
    if (!open) return;
    setSent(false);
    setError(null);
    (async () => {
      const accessToken = await auth.getToken();
      const [exams, p] = await Promise.all([
        listExamTargets(accessToken).catch(() => [] as typeof examTargets),
        getGoalProfile(accessToken).catch(() => null),
      ]);
      setExamTargets(exams);
      setGoalProfile(p);
      setMessage(exams.length > 0 ? buildExamMessage(teacherName, p, exams[0]) : buildStandardMessage(teacherName, p));
    })();
  }, [open, teacherName]);

  // "While you wait" similar offerings
  const { data: similarData } = useOneToOneSearch({ q: goalProfile?.subject ?? undefined, size: 6 }, sent && !!goalProfile?.subject);
  const similar = (similarData?.content ?? []).filter((o) => o.teacherHandle && o.teacherHandle !== teacherHandle).slice(0, 3);

  const handleSend = async () => {
    if (!message.trim()) return;
    const accessToken = await auth.getToken();
    setIsSending(true);
    setError(null);
    try {
      await createInquiry(accessToken, {
        teacherUserId,
        subject: `Inquiry to ${displayName}`,
        message: message.trim(),
        offeringId: offeringId ?? undefined,
        gradeBand: goalProfile?.gradeBand ?? undefined,
        curriculum: goalProfile?.curriculum ?? undefined,
        goalType: goalProfile?.goalType ?? undefined,
        modePreference: goalProfile?.modePreference ?? undefined,
        startTimeline: goalProfile?.startTimeline ?? undefined,
      });
      setSent(true);
      refreshProfile(true).catch(() => {});
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to send inquiry';
      if (msg === 'THREAD_EXISTS') setError('You already have an active conversation with this teacher.');
      else if (msg === 'RATE_LIMITED') setError('Too many requests. Please try again in a moment.');
      else if (msg === 'INSUFFICIENT_COINS') {
        setError(
          <>
            You don't have enough BMC coins to send this inquiry.{' '}
            <Link to="/buy-coins" className="font-semibold underline underline-offset-2 hover:opacity-80">
              Top up your coins
            </Link>{' '}
            to continue.
          </>,
        );
      } else setError('Could not send your inquiry. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const ctx: { label: string; value: string }[] = [];
  if (goalProfile?.subject) ctx.push({ label: 'Subject', value: [goalProfile.subject, gradeLabel(goalProfile.gradeBand) || goalProfile.gradeBand].filter(Boolean).join(' · ') });
  if (goalProfile?.curriculum) ctx.push({ label: 'Curriculum', value: goalProfile.curriculum });
  if (goalProfile?.goalType) ctx.push({ label: 'Goal', value: goalProfile.goalType });
  if (goalProfile?.modePreference) ctx.push({ label: 'Mode', value: modeLabel(goalProfile.modePreference) });
  const bt = budgetText(goalProfile);
  if (bt) ctx.push({ label: 'Budget', value: `${bt} / session` });
  if (goalProfile?.startTimeline) ctx.push({ label: 'Start', value: timelineLabel(goalProfile.startTimeline) });
  if (examTargets[0]) ctx.push({ label: 'Exam', value: `${examTargets[0].examName} · ${Math.max(0, daysUntil(examTargets[0].examDate))} days` });

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-xl p-0 overflow-hidden">
        {sent ? (
          /* ── Post-send confirmation ── */
          <div className="px-6 py-8 text-center">
            {/* Success icon */}
            <div className="relative w-16 h-16 mx-auto mb-4">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                <Check className="w-8 h-8 text-emerald-600" strokeWidth={2.5} />
              </div>
              <Sparkles className="absolute -top-1 -right-1 w-5 h-5 text-amber-400" />
            </div>

            <h3 className="text-lg font-bold text-slate-900">Message sent!</h3>
            <p className="text-sm text-slate-500 mt-1.5 max-w-xs mx-auto">
              {firstName} usually replies within 24 hours. We'll notify you as soon as they respond.
            </p>

            {similar.length > 0 && (
              <div className="mt-6 text-left">
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2.5">
                  While you wait — similar teachers
                </p>
                <div className="space-y-2">
                  {similar.map((o) => (
                    <Link
                      key={o.offeringId}
                      to={o.teacherHandle && o.slug ? `/t/${o.teacherHandle}/${o.slug}` : `/public/offering/${o.offeringId}`}
                      onClick={onClose}
                      className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2.5 hover:border-indigo-300 hover:bg-indigo-50/40 transition-colors"
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center text-xs font-bold text-slate-600 shrink-0">
                        {(o.teacherName ?? o.title).charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-800 truncate">{o.title}</div>
                        <div className="text-xs text-slate-400 truncate">{[o.teacherName, o.subjectName].filter(Boolean).join(' · ')}</div>
                      </div>
                      <ArrowRight className="w-4 h-4 text-indigo-400 shrink-0" />
                    </Link>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-2.5 text-center">
                  Contacting a few teachers in parallel improves your chances of a great match.
                </p>
              </div>
            )}

            <div className="flex gap-2 mt-6">
              <Button variant="outline" className="flex-1" onClick={onClose}>Close</Button>
              <Button className="flex-1" onClick={() => { onClose(); navigate('/student-dashboard'); }}>
                Go to dashboard
              </Button>
            </div>
          </div>
        ) : (
          /* ── Composer ── */
          <>
            {/* Header strip */}
            <div className="px-6 pt-6 pb-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <TeacherAvatar name={displayName} />
                <div className="flex-1 min-w-0">
                  <DialogTitle className="text-base font-semibold text-slate-900 leading-tight">
                    Message {displayName}
                  </DialogTitle>
                  <DialogDescription className="text-xs text-slate-500 mt-0.5">
                    Your learning profile is shared with {firstName} alongside this message.
                  </DialogDescription>
                </div>
                {coinBalance != null && (
                  <div className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700 shrink-0">
                    <Coins className="w-3 h-3" />
                    {coinBalance}
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 space-y-4">
              {/* Context badges */}
              {ctx.length > 0 && (
                <div className="rounded-xl bg-slate-50 border border-slate-100 px-3.5 py-3">
                  <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-2.5">
                    <Lock className="w-3 h-3" />
                    Shared automatically with {firstName}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {ctx.map((c) => <ContextBadge key={c.label} label={c.label} value={c.value} />)}
                  </div>
                </div>
              )}

              {/* Editable message */}
              <div>
                <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-1.5">
                  <Pencil className="w-3 h-3" />
                  Your message
                  <span className="ml-auto">pre-filled · editable</span>
                </div>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={7}
                  placeholder="Write your message…"
                  className="resize-none text-sm leading-relaxed"
                />
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-100 px-3 py-2.5 text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-red-500" />
                  <span>{error}</span>
                </div>
              )}

              {/* Coin cost + CTA */}
              <div className="flex flex-col gap-2">
                <div className="flex gap-2">
                  <Button variant="outline" onClick={onClose} className="flex-1" disabled={isSending}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSend}
                    className="flex-1 gap-2"
                    disabled={isSending || !message.trim()}
                  >
                    {isSending ? (
                      <>
                        <span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        <MessageCircle className="w-4 h-4" />
                        Send message
                      </>
                    )}
                  </Button>
                </div>
                {/* Coin cost footnote */}
                <p className="text-center text-xs text-slate-400">
                  Costs{' '}
                  <span className="inline-flex items-center gap-0.5 font-semibold text-amber-600">
                    <Coins className="w-3 h-3" />{INQUIRY_COST_COINS} coins
                  </span>
                  {' '}· deducted when sent · never expires
                </p>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
